import {authorized, optionallyAuthorized} from "./ApiClient";
import {fromCatalog} from "./catalog";
import {
    PresignedUrl,
    DemoAnalysis,
    ProductDemoViewModel,
    ProductBindingModel,
    ProductFileBindingModel,
    ProductFileViewModel,
    ProductImageViewModel,
    ProductPress,
    ProductVersionBindingModel,
    ProductVersionViewModel,
    ProductViewModel
} from "./models";

const product = (id: string) => `/product/${encodeURIComponent(id)}`;
const version = (productId: string, versionId: string) => `${product(productId)}/version/${encodeURIComponent(versionId)}`;
const file = (productId: string, versionId: string, fileId: string) => `${version(productId, versionId)}/file/${encodeURIComponent(fileId)}`;
const image = (productId: string, imageId: string) => `${product(productId)}/image/${encodeURIComponent(imageId)}`;
const demo = (productId: string, demoId: string) => `${product(productId)}/demo/${encodeURIComponent(demoId)}`;

// The product catalog. Reading published products needs no session, nor does downloadUrl for a released product (an
// Early Access product's downloads need a license for it, an Early Access Version's an Early Access license); the rest
// is for site administrators.
export const ProductsApi = {
    // From the catalog on the CDN (catalog.ts), or the API when that can't be had.
    getPublished: () => fromCatalog(x => x.products, () => optionallyAuthorized<ProductViewModel[]>('GET', '/product')),

    // Every product, with unpublished ones and files that haven't been uploaded (SiteAdmin).
    getAll: () => authorized<ProductViewModel[]>('GET', '/product?includeUnpublished=true'),

    get: (id: string) => authorized<ProductViewModel>('GET', `${product(id)}?includeUnpublished=true`),

    create: (model: ProductBindingModel) => authorized<ProductViewModel>('POST', '/product', model),

    // Fields left out keep their values.
    update: (id: string, model: Partial<ProductBindingModel>) => authorized<ProductViewModel>('PUT', product(id), model),

    // 409 while licenses for the product exist.
    remove: (id: string) => authorized<void>('DELETE', product(id)),

    addVersion: (productId: string, model: ProductVersionBindingModel) =>
        authorized<ProductVersionViewModel>('POST', `${product(productId)}/version`, model),

    updateVersion: (productId: string, versionId: string, model: ProductVersionBindingModel) =>
        authorized<ProductVersionViewModel>('PUT', version(productId, versionId), model),

    // 409 for the current version (delisting it).
    setDelisted: (productId: string, versionId: string, delisted: boolean) =>
        authorized<ProductVersionViewModel>('PUT', version(productId, versionId), { delisted }),

    // 409 for the current version.
    removeVersion: (productId: string, versionId: string) => authorized<void>('DELETE', version(productId, versionId)),

    // 409 until the version has an uploaded file. Only replaces the current version of its own kind (released or Early Access).
    makeCurrent: (productId: string, versionId: string) =>
        authorized<ProductVersionViewModel>('PUT', `${version(productId, versionId)}/current`),

    // Adds the file's record: PUT the file to upload.url, then call completeFile.
    addFile: (productId: string, versionId: string, model: ProductFileBindingModel & { fileName: string }) =>
        authorized<{ file: ProductFileViewModel, upload: PresignedUrl }>('POST', `${version(productId, versionId)}/file`, model),

    // Confirms the upload, which records the size and makes the file downloadable. 409 if nothing was uploaded.
    completeFile: (productId: string, versionId: string, fileId: string) =>
        authorized<ProductFileViewModel>('POST', `${file(productId, versionId, fileId)}/complete`),

    updateFile: (productId: string, versionId: string, fileId: string, changes: Partial<ProductFileBindingModel>) =>
        authorized<ProductFileViewModel>('PUT', file(productId, versionId, fileId), changes),

    // 409 for the current version's last uploaded file.
    removeFile: (productId: string, versionId: string, fileId: string) => authorized<void>('DELETE', file(productId, versionId, fileId)),

    // A published product's Early Access Versions, newest first: for Early Access Users with an Early Access license
    // for it, and administrators (403 no_license otherwise).
    earlyAccessVersions: (productId: string) =>
        authorized<ProductVersionViewModel[]>('GET', `${product(productId)}/early-access-versions`),

    // For owners of a license for the product (403 otherwise) and administrators. The URL lasts a few minutes.
    downloadUrl: (productId: string, versionId: string, fileId: string) =>
        optionallyAuthorized<PresignedUrl>('GET', `${file(productId, versionId, fileId)}/download-url`),

    // Adds the image's record: PUT the file to upload.url.
    addImage: (productId: string, contentType: string, altText: string, size?: { width: number, height: number }) =>
        authorized<{ image: ProductImageViewModel, upload: PresignedUrl }>('POST', `${product(productId)}/image`, { contentType, altText, ...size }),

    updateImage: (productId: string, imageId: string, changes: { altText?: string, sortOrder?: number, width?: number, height?: number }) =>
        authorized<ProductImageViewModel>('PUT', image(productId, imageId), changes),

    // A new picture for the image, keeping its alt text and place: PUT it to upload.url, then call completeImageReplacement.
    replaceImage: (productId: string, imageId: string, contentType: string, size?: { width: number, height: number }) =>
        authorized<{ image: ProductImageViewModel, upload: PresignedUrl }>('POST', `${image(productId, imageId)}/replacement`, { contentType, ...size }),

    // Shows the uploaded replacement in the old picture's place. 409 (image_not_uploaded) until it has been uploaded.
    completeImageReplacement: (productId: string, imageId: string) =>
        authorized<ProductImageViewModel>('POST', `${image(productId, imageId)}/replacement/complete`),

    removeImage: (productId: string, imageId: string) => authorized<void>('DELETE', image(productId, imageId)),

    // Adds the demo's record: PUT the file (exactly size bytes) to upload.url, then completeDemo puts it on the site.
    addDemo: (productId: string, contentType: string, size: number, title: string, description: string) =>
        authorized<{ demo: ProductDemoViewModel, upload: PresignedUrl }>('POST', `${product(productId)}/demo`, { contentType, size, title, description }),

    completeDemo: (productId: string, demoId: string) => authorized<ProductDemoViewModel>('POST', `${demo(productId, demoId)}/complete`),

    updateDemo: (productId: string, demoId: string, changes: { title?: string, description?: string, sortOrder?: number }) =>
        authorized<ProductDemoViewModel>('PUT', demo(productId, demoId), changes),

    removeDemo: (productId: string, demoId: string) => authorized<void>('DELETE', demo(productId, demoId)),
    // The demo's waveform and spectrum, for the player (only when the demo says hasAnalysis).
    getDemoAnalysis: (productId: string, demoId: string) => optionallyAuthorized<DemoAnalysis>('GET', `${demo(productId, demoId)}/analysis`),
    // From the administration page, which works it out from the file (components/demoAnalysis.ts).
    setDemoAnalysis: (productId: string, demoId: string, analysis: DemoAnalysis) =>
        authorized<ProductDemoViewModel>('PUT', `${demo(productId, demoId)}/analysis`, analysis),

    // The press kit (/press): its switch and feature summary; either may be left out.
    updatePress: (productId: string, changes: { published?: boolean, summary?: string }) =>
        authorized<ProductPress>('PUT', `${product(productId)}/press`, changes),

    // Zips the product's images, in order, with their alt texts, replacing the pack before. 409 product_has_no_images.
    buildPressImagePack: (productId: string) => authorized<ProductPress>('POST', `${product(productId)}/press/image-pack`),

    // The reviewer's notes PDF: PUT it (exactly size bytes) to upload.url, then completeReviewerNotes puts it in the
    // press kit, in place of the one before.
    startReviewerNotes: (productId: string, fileName: string, size: number) =>
        authorized<{ upload: PresignedUrl }>('POST', `${product(productId)}/press/reviewer-notes`, { fileName, size }),

    // 400 not_a_pdf if it isn't one.
    completeReviewerNotes: (productId: string) => authorized<ProductPress>('POST', `${product(productId)}/press/reviewer-notes/complete`),

    removeReviewerNotes: (productId: string) => authorized<ProductPress>('DELETE', `${product(productId)}/press/reviewer-notes`)
};
